import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";
import { tenantFetch } from "../lib/api-client";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";

interface IntelligenceData {
  brand_id: string;
  brand_name: string;
  period_days: number;
  total_active_listings: number;
  total_sold_period: number;
  estimated_annual_gmv: number;
  avg_sale_price: number;
  sell_through_rate: number;
  avg_rrp_percentage: number | null;
  platform_breakdown: Record<string, number>;
  condition_breakdown: Record<string, number>;
  category_breakdown: Record<string, number>;
  daily_trend: {
    date: string;
    active_listings: number;
    sales: number;
    avg_price: number | null;
  }[];
}

interface ListingItem {
  id: string;
  title: string;
  price: number | null;
  price_gbp: number | null;
  currency: string;
  platform: string;
  condition: string;
  listing_type: string;
  listing_url: string;
  image_urls: string[];
  category: string;
  size: string;
  first_seen_at: string;
  last_seen_at: string;
}

interface ListingsResponse {
  items: ListingItem[];
  total: number;
  page: number;
  pages: number;
}

export const Route = createFileRoute("/market-intelligence")({
  component: MarketIntelligenceDashboard,
});

function MarketIntelligenceDashboard() {
  const { currentTenant } = useTenant();
  const [data, setData] = useState<IntelligenceData | null>(null);
  const [listings, setListings] = useState<ListingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [brandNotFound, setBrandNotFound] = useState(false);
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("30d");
  const [listingsPage, setListingsPage] = useState(1);
  const [listingsPlatform, setListingsPlatform] = useState("");
  const [listingsSearch, setListingsSearch] = useState("");
  const [listingsSort, setListingsSort] = useState("newest");

  useEffect(() => {
    loadIntelligence();
  }, [currentTenant.id, period]);

  useEffect(() => {
    if (data) {
      loadListings();
    }
  }, [
    data,
    currentTenant.id,
    listingsPage,
    listingsPlatform,
    listingsSort,
    listingsSearch,
  ]);

  const loadIntelligence = async () => {
    setLoading(true);
    setBrandNotFound(false);
    try {
      const res = await tenantFetch(
        `/api/v1/market-intelligence?period=${period}`,
      );
      if (res.ok) {
        const result = await res.json();
        setData(result.data);
      } else if (res.status === 404) {
        setBrandNotFound(true);
        setData(null);
      } else {
        // Fallback to demo
        setData(getDemoIntelligence(currentTenant.id, period));
      }
    } catch {
      setData(getDemoIntelligence(currentTenant.id, period));
    } finally {
      setLoading(false);
    }
  };

  const loadListings = async () => {
    try {
      const params = new URLSearchParams({
        page: String(listingsPage),
        limit: "24",
        sort: listingsSort,
      });
      if (listingsPlatform) params.set("platform", listingsPlatform);
      if (listingsSearch) params.set("q", listingsSearch);

      const res = await tenantFetch(
        `/api/v1/market-intelligence/listings?${params}`,
      );
      if (res.ok) {
        const result = await res.json();
        setListings(result.data);
      } else {
        setListings(getDemoListings(currentTenant.id));
      }
    } catch {
      setListings(getDemoListings(currentTenant.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: currentTenant.color,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  if (brandNotFound) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Market Intelligence
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            Resale market monitoring powered by real marketplace data
          </p>
        </div>
        <div
          className="p-8 text-center"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <div
            className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full"
            style={{ backgroundColor: currentTenant.color + "15" }}
          >
            <MarketIcon color={currentTenant.color} />
          </div>
          <h2 className="text-lg font-light mb-2" style={{ color: "#171717" }}>
            Market Intelligence is not yet enabled for {currentTenant.name}
          </h2>
          <p className="text-sm mb-6" style={{ color: "#737373" }}>
            Contact Pentatonic to enable resale market monitoring for your
            brand. We track listings across eBay, Vinted, Depop, Vestiaire
            Collective, and TheRealReal.
          </p>
          <a
            href="mailto:support@pentatonic.com?subject=Enable Market Intelligence"
            className="inline-block px-6 py-3 text-sm text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: currentTenant.color }}
          >
            Request Access
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const COLORS = [
    "#00FBA9",
    currentTenant.color,
    "#3B82F6",
    "#8B5CF6",
    "#F59E0B",
    "#EF4444",
    "#06B6D4",
  ];

  const platformData = Object.entries(data.platform_breakdown)
    .map(([name, count]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const conditionData = Object.entries(data.condition_breakdown)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const categoryData = Object.entries(data.category_breakdown)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const totalPlatformListings = platformData.reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Market Intelligence
          </h1>
          <p className="text-sm mt-1" style={{ color: "#737373" }}>
            How {data.brand_name || currentTenant.name} appears across UK resale
            marketplaces
          </p>
        </div>
        <div className="flex gap-2">
          {(["7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-light transition-colors ${period === p ? "text-white" : "border"}`}
              style={{
                backgroundColor:
                  period === p ? currentTenant.color : "transparent",
                borderColor: period !== p ? "#E5E5E5" : "transparent",
                color: period === p ? "#fff" : "#525252",
              }}
            >
              {p === "7d" ? "7 Days" : p === "30d" ? "30 Days" : "90 Days"}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="Active Listings"
          value={data.total_active_listings.toLocaleString()}
          subtext="Across UK platforms"
          color={currentTenant.color}
        />
        <MetricCard
          label="Resale GMV"
          value={`£${formatCompact(data.estimated_annual_gmv)}`}
          subtext="Total value of UK listings"
          color="#00FBA9"
        />
        <MetricCard
          label="Avg Resale Price"
          value={`£${data.avg_sale_price.toFixed(0)}`}
          subtext="Mean listing price (GBP)"
          color="#3B82F6"
        />
        <MetricCard
          label="RRP Retention"
          value={
            data.avg_rrp_percentage ? `${data.avg_rrp_percentage}%` : "N/A"
          }
          subtext="Avg % of original RRP"
          color="#8B5CF6"
        />
      </div>

      {/* Trend Chart */}
      <div
        className="p-6"
        style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
      >
        <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
          Daily Activity
        </h2>
        <div className="h-64">
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={100}
            minHeight={100}
          >
            <AreaChart data={data.daily_trend}>
              <defs>
                <linearGradient id="activeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={currentTenant.color}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={currentTenant.color}
                    stopOpacity={0}
                  />
                </linearGradient>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00FBA9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00FBA9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke="#737373"
                tickFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <YAxis tick={{ fontSize: 11 }} stroke="#737373" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#171717",
                  border: "none",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#737373" }}
                itemStyle={{ color: "#fff" }}
                labelFormatter={(v) => {
                  const d = new Date(v);
                  return d.toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  });
                }}
              />
              <Area
                type="monotone"
                dataKey="active_listings"
                stroke={currentTenant.color}
                strokeWidth={2}
                fill="url(#activeGrad)"
                name="Active Listings"
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="#00FBA9"
                strokeWidth={2}
                fill="url(#salesGrad)"
                name="Sales"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Breakdown */}
        <div
          className="p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            By Platform
          </h2>
          <div className="h-48 flex items-center justify-center">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <PieChart>
                <Pie
                  data={platformData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {platformData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    "Listings",
                  ]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  itemStyle={{ color: "#fff" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {platformData.map((p, i) => (
              <div
                key={p.name}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span style={{ color: "#525252" }}>{p.name}</span>
                </div>
                <span className="font-light" style={{ color: "#171717" }}>
                  {totalPlatformListings > 0
                    ? Math.round((p.count / totalPlatformListings) * 100)
                    : 0}
                  %
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Condition Breakdown */}
        <div
          className="p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            By Condition
          </h2>
          <div className="h-64">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={100}
              minHeight={100}
            >
              <BarChart data={conditionData} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#737373" />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  stroke="#737373"
                  width={80}
                />
                <Tooltip
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    "Listings",
                  ]}
                  contentStyle={{
                    backgroundColor: "#171717",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  itemStyle={{ color: "#fff" }}
                />
                <Bar
                  dataKey="count"
                  fill={currentTenant.color}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown */}
        <div
          className="p-6"
          style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
        >
          <h2 className="text-lg font-light mb-4" style={{ color: "#171717" }}>
            Top Categories
          </h2>
          <div className="space-y-3">
            {categoryData.slice(0, 8).map((cat, i) => {
              const maxCount = categoryData[0]?.count || 1;
              return (
                <div key={cat.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span style={{ color: "#525252" }}>
                      {cat.name || "Uncategorized"}
                    </span>
                    <span className="font-light" style={{ color: "#171717" }}>
                      {cat.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${(cat.count / maxCount) * 100}%`,
                        backgroundColor: COLORS[i % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Listings — Product Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-light" style={{ color: "#171717" }}>
              Listings
            </h2>
            {currentTenant?.id === "arcteryx" ? (
              <p className="text-xs mt-0.5" style={{ color: "#737373" }}>
                248 results
              </p>
            ) : listings && (
              <p className="text-xs mt-0.5" style={{ color: "#737373" }}>
                {listings.total.toLocaleString()} results
              </p>
            )}
          </div>
          {currentTenant?.id !== "arcteryx" && (
            <div className="flex gap-3 items-center">
              <input
                type="text"
                placeholder="Search listings..."
                value={listingsSearch}
                onChange={(e) => {
                  setListingsSearch(e.target.value);
                  setListingsPage(1);
                }}
                className="px-3 py-1.5 text-sm border"
                style={{ borderColor: "#E5E5E5", color: "#171717" }}
              />
              <select
                value={listingsPlatform}
                onChange={(e) => {
                  setListingsPlatform(e.target.value);
                  setListingsPage(1);
                }}
                className="px-3 py-1.5 text-sm border"
                style={{ borderColor: "#E5E5E5", color: "#525252" }}
              >
                <option value="">All Platforms</option>
                {platformData.map((p) => (
                  <option key={p.name} value={p.name.toLowerCase()}>
                    {p.name}
                  </option>
                ))}
              </select>
              <select
                value={listingsSort}
                onChange={(e) => {
                  setListingsSort(e.target.value);
                  setListingsPage(1);
                }}
                className="px-3 py-1.5 text-sm border"
                style={{ borderColor: "#E5E5E5", color: "#525252" }}
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
              </select>
            </div>
          )}
        </div>

        {currentTenant?.id === "arcteryx" ? (
          <ArcterxListingsGrid />
        ) : listings && listings.items.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {listings.items.map((item) => (
                <a
                  key={item.id}
                  href={item.listing_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-white border transition-shadow hover:shadow-md"
                  style={{ borderColor: "#E5E5E5" }}
                >
                  {/* Image */}
                  <div className="relative aspect-square bg-gray-100 overflow-hidden">
                    {item.image_urls?.[0] ? (
                      <img
                        src={item.image_urls[0].replace("s-l225", "s-l500")}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const el = e.target as HTMLImageElement;
                          el.src = item.image_urls[0];
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          className="w-12 h-12"
                          style={{ color: "#D4D4D4" }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                    {/* Platform badge overlay */}
                    <div className="absolute top-2 left-2">
                      <PlatformBadge platform={item.platform} />
                    </div>
                    {/* Listing type badge */}
                    {item.listing_type === "sold" && (
                      <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-[10px] px-2 py-0.5 uppercase tracking-wider">
                        Sold
                      </div>
                    )}
                  </div>
                  {/* Details */}
                  <div className="p-3">
                    <p
                      className="text-xs font-light leading-snug mb-2 line-clamp-2"
                      style={{ color: "#171717", minHeight: "2.5em" }}
                      title={item.title}
                    >
                      {item.title}
                    </p>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span
                        className="text-base font-medium"
                        style={{ color: "#171717" }}
                      >
                        £{item.price_gbp?.toFixed(2) ?? "—"}
                      </span>
                      {item.price && item.currency !== "GBP" && (
                        <span
                          className="text-[10px]"
                          style={{ color: "#A3A3A3" }}
                        >
                          {item.currency === "USD"
                            ? "$"
                            : item.currency === "EUR"
                              ? "€"
                              : ""}
                          {item.price.toFixed(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.condition && (
                        <span
                          className="text-[10px] px-1.5 py-0.5"
                          style={{
                            backgroundColor: "#F5F5F5",
                            color: "#525252",
                          }}
                        >
                          {item.condition}
                        </span>
                      )}
                      {item.size && (
                        <span
                          className="text-[10px] px-1.5 py-0.5"
                          style={{
                            backgroundColor: "#F5F5F5",
                            color: "#525252",
                          }}
                        >
                          Size {item.size}
                        </span>
                      )}
                      {item.category && (
                        <span
                          className="text-[10px] px-1.5 py-0.5"
                          style={{
                            backgroundColor: "#F5F5F5",
                            color: "#525252",
                          }}
                        >
                          {item.category}
                        </span>
                      )}
                    </div>
                    <p
                      className="text-[10px] mt-2"
                      style={{ color: "#A3A3A3" }}
                    >
                      Listed{" "}
                      {item.first_seen_at
                        ? formatDate(item.first_seen_at)
                        : "—"}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            {/* Pagination */}
            {listings.pages > 1 && (
              <div
                className="flex items-center justify-between mt-6 pt-4 border-t"
                style={{ borderColor: "#E5E5E5" }}
              >
                <span className="text-sm" style={{ color: "#737373" }}>
                  Page {listings.page} of {listings.pages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setListingsPage(Math.max(1, listingsPage - 1))
                    }
                    disabled={listingsPage <= 1}
                    className="px-4 py-2 text-sm border disabled:opacity-30 transition-colors hover:bg-gray-50"
                    style={{ borderColor: "#E5E5E5", color: "#525252" }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setListingsPage(
                        Math.min(listings.pages, listingsPage + 1),
                      )
                    }
                    disabled={listingsPage >= listings.pages}
                    className="px-4 py-2 text-sm border disabled:opacity-30 transition-colors hover:bg-gray-50"
                    style={{ borderColor: "#E5E5E5", color: "#525252" }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            className="text-center py-12"
            style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
          >
            <p className="text-sm" style={{ color: "#737373" }}>
              No listings found
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const ARCTERYX_PRODUCTS = [
  {
    id: "arc-1",
    title: "Arc'teryx Beta AR Jacket — Men's Rigel, Medium",
    price: 148,
    platform: "eBay",
    condition: "Excellent",
    size: "M",
    category: "Shell",
    image: "/images/products/arcteryx/beta-ar-jacket.jpeg",
    listed: "2 days ago",
  },
  {
    id: "arc-2",
    title: "Arc'teryx Atom LT Hoody — Women's Black, Small",
    price: 72,
    platform: "Vinted",
    condition: "Good",
    size: "S",
    category: "Insulated",
    image: "/images/products/arcteryx/atom-lt-hoody.jpeg",
    listed: "4 days ago",
  },
  {
    id: "arc-3",
    title: "Arc'teryx Alpha SV Jacket — Men's Infrared, Large",
    price: 201,
    platform: "eBay",
    condition: "Like New",
    size: "L",
    category: "Shell",
    image: "/images/products/arcteryx/alpha-sv-jacket.jpeg",
    listed: "1 day ago",
  },
  {
    id: "arc-4",
    title: "Arc'teryx Beta LT Jacket — Women's Cobalt Moon, XS",
    price: 119,
    platform: "Depop",
    condition: "Good",
    size: "XS",
    category: "Shell",
    image: "/images/products/arcteryx/beta-lt-jacket.jpeg",
    listed: "6 days ago",
  },
  {
    id: "arc-5",
    title: "Arc'teryx Cerium LT Hoody — Men's Yukon, Medium",
    price: 164,
    platform: "eBay",
    condition: "Excellent",
    size: "M",
    category: "Insulated",
    image: "/images/products/arcteryx/cerium-lt-jacket.jpeg",
    listed: "3 days ago",
  },
  {
    id: "arc-6",
    title: "Arc'teryx Covert Cardigan — Women's Vultee, Small",
    price: 88,
    platform: "Vinted",
    condition: "Good",
    size: "S",
    category: "Fleece",
    image: "/images/products/arcteryx/covert-cardigan.jpeg",
    listed: "5 days ago",
  },
  {
    id: "arc-7",
    title: "Arc'teryx Delta LT Zip-Neck — Men's Orion, Large",
    price: 67,
    platform: "Depop",
    condition: "Good",
    size: "L",
    category: "Fleece",
    image: "/images/products/arcteryx/delta-lt-zip-neck.jpeg",
    listed: "1 week ago",
  },
  {
    id: "arc-8",
    title: "Arc'teryx Gamma LT Pants — Men's Black, 32",
    price: 53,
    platform: "eBay",
    condition: "Fair",
    size: "32",
    category: "Bottoms",
    image: "/images/products/arcteryx/gamma-lt-pants.jpeg",
    listed: "3 days ago",
  },
  {
    id: "arc-9",
    title: "Arc'teryx Proton FL Hoody — Women's Enigma, XS",
    price: 93,
    platform: "Vinted",
    condition: "Excellent",
    size: "XS",
    category: "Insulated",
    image: "/images/products/arcteryx/proton-fl-hoody.jpeg",
    listed: "2 days ago",
  },
  {
    id: "arc-10",
    title: "Arc'teryx Zeta SL Jacket — Men's Pilot, Medium",
    price: 89,
    platform: "Depop",
    condition: "Good",
    size: "M",
    category: "Shell",
    image: "/images/products/arcteryx/zeta-sl-jacket.jpeg",
    listed: "4 days ago",
  },
];

const PLATFORM_COLORS: Record<string, string> = {
  ebay: "#E53E3E",
  vinted: "#00BA88",
  depop: "#FF4040",
  stockx: "#23E5DB",
};

function ArcterxListingsGrid() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {ARCTERYX_PRODUCTS.map((item) => {
        const platformKey = item.platform.toLowerCase();
        const badgeColor = PLATFORM_COLORS[platformKey] ?? "#737373";
        return (
          <div
            key={item.id}
            className="group block bg-white border"
            style={{ borderColor: "#E5E5E5" }}
          >
            {/* Image */}
            <div className="relative aspect-square bg-gray-100 overflow-hidden">
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {/* Platform badge */}
              <div className="absolute top-2 left-2">
                <span
                  className="text-[10px] px-2 py-0.5 font-medium uppercase tracking-wider text-white"
                  style={{ backgroundColor: badgeColor }}
                >
                  {item.platform}
                </span>
              </div>
            </div>
            {/* Details */}
            <div className="p-3">
              <p
                className="text-xs font-light leading-snug mb-2 line-clamp-2"
                style={{ color: "#171717", minHeight: "2.5em" }}
              >
                {item.title}
              </p>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-base font-medium" style={{ color: "#171717" }}>
                  ${item.price.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className="text-[10px] px-1.5 py-0.5"
                  style={{ backgroundColor: "#F5F5F5", color: "#525252" }}
                >
                  {item.condition}
                </span>
                <span
                  className="text-[10px] px-1.5 py-0.5"
                  style={{ backgroundColor: "#F5F5F5", color: "#525252" }}
                >
                  Size {item.size}
                </span>
                <span
                  className="text-[10px] px-1.5 py-0.5"
                  style={{ backgroundColor: "#F5F5F5", color: "#525252" }}
                >
                  {item.category}
                </span>
              </div>
              <p className="text-[10px] mt-2" style={{ color: "#A3A3A3" }}>
                Listed {item.listed}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Subcomponents ---

function MetricCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string;
  value: string;
  subtext: string;
  color: string;
}) {
  return (
    <div
      className="p-5"
      style={{ backgroundColor: "#FAFAFA", border: "1px solid #E5E5E5" }}
    >
      <div
        className="text-xs font-light uppercase mb-2"
        style={{ color: "#737373" }}
      >
        {label}
      </div>
      <div className="text-2xl font-semibold mb-1" style={{ color }}>
        {value}
      </div>
      <span className="text-xs" style={{ color: "#737373" }}>
        {subtext}
      </span>
    </div>
  );
}

function PlatformBadge({ platform }: { platform: string }) {
  const colors: Record<string, string> = {
    ebay: "#E53238",
    vinted: "#09B1BA",
    depop: "#FF2300",
    therealreal: "#000000",
    vestiaire: "#1A1A1A",
    grailed: "#000000",
    thredup: "#49B36E",
    pentatonic: "#00C896",
  };
  const bg = colors[platform.toLowerCase()] || "#737373";
  return (
    <span
      className="text-xs px-2 py-0.5 rounded text-white"
      style={{ backgroundColor: bg }}
    >
      {platform.charAt(0).toUpperCase() + platform.slice(1)}
    </span>
  );
}

function MarketIcon({ color }: { color: string }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12H22" strokeLinecap="round" />
      <path
        d="M12 2C14.5 4.5 16 8 16 12C16 16 14.5 19.5 12 22"
        strokeLinecap="round"
      />
      <path
        d="M12 2C9.5 4.5 8 8 8 12C8 16 9.5 19.5 12 22"
        strokeLinecap="round"
      />
      <path d="M4 7H20" strokeLinecap="round" />
      <path d="M4 17H20" strokeLinecap="round" />
    </svg>
  );
}

// --- Helpers ---

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

// --- Demo data fallbacks ---

function getDemoIntelligence(
  tenantId: string,
  period: string,
): IntelligenceData {
  const days = period === "7d" ? 7 : period === "90d" ? 90 : 30;
  const brandName = tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
  const baseListings =
    tenantId === "nike"
      ? 185000
      : tenantId === "adidas"
        ? 142000
        : tenantId === "arcteryx"
          ? 8500
          : 25000;
  const baseSold = Math.round(baseListings * 0.12 * (days / 30));
  const avgPrice =
    tenantId === "arcteryx"
      ? 145
      : tenantId === "nike"
        ? 62
        : tenantId === "adidas"
          ? 55
          : 85;

  const daily_trend = [];
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    daily_trend.push({
      date: date.toISOString().split("T")[0],
      active_listings: baseListings + Math.floor(Math.random() * 2000 - 1000),
      sales: Math.floor(baseSold / days + Math.random() * 20 - 10),
      avg_price: avgPrice + Math.floor(Math.random() * 20 - 10),
    });
  }

  return {
    brand_id: tenantId,
    brand_name: brandName,
    period_days: days,
    total_active_listings: baseListings,
    total_sold_period: baseSold,
    estimated_annual_gmv: baseSold * avgPrice * (365 / days),
    avg_sale_price: avgPrice,
    sell_through_rate: 12.4,
    avg_rrp_percentage:
      tenantId === "arcteryx" ? 62 : tenantId === "nike" ? 48 : 45,
    platform_breakdown: {
      ebay: Math.round(baseListings * 0.65),
      vinted: Math.round(baseListings * 0.18),
      depop: Math.round(baseListings * 0.08),
      therealreal: Math.round(baseListings * 0.05),
      vestiaire: Math.round(baseListings * 0.04),
    },
    condition_breakdown: {
      "New with tags": Math.round(baseListings * 0.15),
      "Like New": Math.round(baseListings * 0.22),
      Used: Math.round(baseListings * 0.35),
      Good: Math.round(baseListings * 0.2),
      Fair: Math.round(baseListings * 0.08),
    },
    category_breakdown: {
      Shoes: Math.round(baseListings * 0.35),
      "Jackets & Coats": Math.round(baseListings * 0.2),
      Tops: Math.round(baseListings * 0.15),
      Pants: Math.round(baseListings * 0.12),
      Accessories: Math.round(baseListings * 0.1),
      Bags: Math.round(baseListings * 0.08),
    },
    daily_trend,
  };
}

function getDemoListings(tenantId: string): ListingsResponse {
  const brandName = tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
  const platforms = ["ebay", "vinted", "depop", "therealreal"];
  const conditions = ["New with tags", "Like New", "Used", "Good"];
  const items: ListingItem[] = [];

  for (let i = 0; i < 15; i++) {
    const platform = platforms[i % platforms.length];
    items.push({
      id: `demo-${i}`,
      title: `${brandName} ${["Running Shoe", "Jacket", "T-Shirt", "Hoodie", "Shorts", "Backpack", "Cap", "Sneaker", "Pants", "Vest", "Polo", "Slides", "Tracksuit", "Beanie", "Socks"][i]} - Size ${["S", "M", "L", "XL", "10", "11", "42", "44"][i % 8]}`,
      price: 25 + Math.floor(Math.random() * 150),
      price_gbp: 25 + Math.floor(Math.random() * 150),
      currency: "GBP",
      platform,
      condition: conditions[i % conditions.length],
      listing_type: "active",
      listing_url: "",
      image_urls: [],
      category: ["Shoes", "Jackets", "Tops", "Bottoms", "Accessories"][i % 5],
      size: ["S", "M", "L", "XL", "10"][i % 5],
      first_seen_at: new Date(
        Date.now() - Math.random() * 30 * 86400000,
      ).toISOString(),
      last_seen_at: new Date().toISOString(),
    });
  }

  return { items, total: 1500, page: 1, pages: 100 };
}
